import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonContent,
  IonIcon,
  IonList,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  useIonAlert,
} from "@ionic/react";
import {
  cashOutline,
  notificationsOutline,
  checkmarkDoneOutline,
  peopleOutline,
  briefcaseOutline,
  checkmarkCircleOutline,
  removeCircleOutline,
  alertCircleOutline,
} from "ionicons/icons";
import {
  collection,
  collectionGroup,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { useHistory } from "react-router-dom";
import { db } from "../firebase";
import { ProfileContext } from "../context/ProfileContext";
import TabNavigator from "../components/TabNavigator";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { PUSH_NOTIFICATION_TYPES } from "../functions/postActionTypes";
import { isDesktop } from "../utils/platformUtils";
import NotificationsDesktop from "./desktop/NotificationsDesktop";
import { PaymentReceipt } from "../types/payment";
import { formatCurrency } from "../utils/paymentCalculations";
import { useNotificationTapHandlers } from "../hooks/useNotificationTapHandlers";

interface AppNotification {
  id: string;
  type?: string;
  title?: string;
  body?: string;
  createdAt?: any;
  isRead?: boolean;
  readAt?: any;
  fromUID?: string;
  toUID?: string;
  additionalDetails?: any;
}

const formatNotificationTime = (createdAt: any) => {
  try {
    if (!createdAt) return "";

    const date = typeof createdAt?.toDate === "function" ? createdAt.toDate() : new Date(createdAt);

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;

    return date.toLocaleDateString();
  } catch (error) {
    return "";
  }
};

const getNotificationIcon = (type?: string) => {
  switch (type) {
    case PUSH_NOTIFICATION_TYPES.AssociationRequest:
    case PUSH_NOTIFICATION_TYPES.SchedulerNewAssociateFromInvite:
      return peopleOutline;

    case PUSH_NOTIFICATION_TYPES.WerkerJobAvailable:
    case PUSH_NOTIFICATION_TYPES.WerkerJobReminder:
      return briefcaseOutline;

    case PUSH_NOTIFICATION_TYPES.SchedulerAddedWerkerToShift:
      return checkmarkCircleOutline;

    case PUSH_NOTIFICATION_TYPES.SchedulerRemovedWerkerFromShift:
      return removeCircleOutline;

    case PUSH_NOTIFICATION_TYPES.SchedulerFilledJobs:
    case PUSH_NOTIFICATION_TYPES.SchedulerUnFilledJobs:
      return alertCircleOutline;

    default:
      return notificationsOutline;
  }
};

const NotificationsMobile: React.FC = () => {
  const { profile } = useContext(ProfileContext) as any;

  const [activeTab, setActiveTab] = useState<'notifications' | 'receipts'>('notifications');
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [receipts, setReceipts] = useState<PaymentReceipt[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [receiptsLoading, setReceiptsLoading] = useState(true);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const uid = profile?.UserId;
  const history = useHistory();
  useIonAlert();

  // Retry counters: a cold launch via push-notification tap can attach these
  // listeners just ahead of the Firestore auth token settling, which surfaces
  // as a permission-denied error that onSnapshot won't retry on its own.
  const [notifRetryTick, setNotifRetryTick] = useState(0);
  const notifRetriedRef = useRef(false);
  const [receiptsRetryTick, setReceiptsRetryTick] = useState(0);
  const receiptsRetriedRef = useRef(false);

  useEffect(() => {
    notifRetriedRef.current = false;
    receiptsRetriedRef.current = false;
  }, [uid]);

  // ── Standard notifications listener ──────────────────────────────────────────
  useEffect(() => {
    if (!uid) {
      setNotifications([]);
      setNotificationsLoading(false);
      return;
    }

    const q = query(collection(db, `users/${uid}/notifications`), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next: AppNotification[] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setNotifications(next);
        setNotificationsLoading(false);
      },
      (err) => {
        Sentry.captureException(err, { tags: { area: "notifications-listener" } });
        setNotificationsLoading(false);
        if (!notifRetriedRef.current) {
          notifRetriedRef.current = true;
          setTimeout(() => setNotifRetryTick((t) => t + 1), 1500);
        }
      }
    );

    return () => unsubscribe();
  }, [uid, notifRetryTick]);

  // ── Payment receipts listener (werker view of scheduler's docs) ───────────────
  useEffect(() => {
    if (!uid) {
      setReceipts([]);
      setReceiptsLoading(false);
      return;
    }

    const q = query(
      collectionGroup(db, 'paymentReceipts'),
      where('werkerUid', '==', uid)
      // orderBy intentionally omitted — sorting client-side to avoid composite index requirement
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next: PaymentReceipt[] = snapshot.docs
          .map((d) => d.data() as PaymentReceipt)
          .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
        setReceipts(next);
        setReceiptsLoading(false);
      },
      (err) => {
        console.error('paymentReceipts listener error:', err);
        Sentry.captureException(err, { tags: { area: "receipts-listener" } });
        setReceiptsLoading(false);
        if (!receiptsRetriedRef.current) {
          receiptsRetriedRef.current = true;
          setTimeout(() => setReceiptsRetryTick((t) => t + 1), 1500);
        }
      }
    );

    return () => unsubscribe();
  }, [uid, receiptsRetryTick]);
  // Reconcile notification counter with actual unread count on every snapshot update
  useEffect(() => {
    if (!uid || notificationsLoading) return;
    const actualCount = notifications.filter((n) => !n.isRead).length;
    setDoc(
      doc(db, `users/${uid}/users_NotificationsCount`, uid),
      { notificationsCount: actualCount, uid },
      { merge: true }
    ).catch(() => {});
  }, [uid, notificationsLoading, notifications]);

  // Reconcile receipts counter with actual unread count on every snapshot update
  useEffect(() => {
    if (!uid || receiptsLoading) return;
    const actualCount = receipts.filter((r) => !r.werkerIsRead).length;
    setDoc(
      doc(db, `users/${uid}/users_PaymentReceiptsCount`, uid),
      { paymentReceiptsUnreadCount: actualCount, uid },
      { merge: true }
    ).catch(() => {});
  }, [uid, receiptsLoading, receipts]);
  // ── Computed values ───────────────────────────────────────────────────────────
  const unreadNotificationsCount = useMemo(() =>
    notifications.filter((item) => !item.isRead).length,
  [notifications]);

  const unreadReceiptsCount = useMemo(() =>
    receipts.filter((r) => !r.werkerIsRead).length,
  [receipts]);

  // ── Standard notification actions ─────────────────────────────────────────────
  const markOneRead = useCallback(async (notificationId: string, alreadyRead?: boolean) => {
    if (!uid || alreadyRead) return;
    try {
      await updateDoc(doc(db, `users/${uid}/notifications/${notificationId}`), {
        isRead: true,
        readAt: serverTimestamp(),
      });
      await setDoc(
        doc(db, `users/${uid}/users_NotificationsCount`, uid),
        { notificationsCount: increment(-1), uid },
        { merge: true }
      );
    } catch (error) {
      // no-op
    }
  }, [uid]);

  const markAllNotificationsRead = async () => {
    if (!uid) return;
    const unreadItems = notifications.filter((item) => !item.isRead);
    if (!unreadItems.length) return;
    try {
      setMarkingAllRead(true);
      const batch = writeBatch(db);
      unreadItems.forEach((item) => {
        batch.update(doc(db, `users/${uid}/notifications/${item.id}`), {
          isRead: true,
          readAt: serverTimestamp(),
        });
      });
      await batch.commit();
      await setDoc(
        doc(db, `users/${uid}/users_NotificationsCount`, uid),
        { notificationsCount: increment(-unreadItems.length), uid },
        { merge: true }
      );
    } catch (error) {
      // no-op
    } finally {
      setMarkingAllRead(false);
    }
  };

  // ── Receipt actions ───────────────────────────────────────────────────────────
  const _markReceiptRead = useCallback(async (receipt: PaymentReceipt) => {
    if (!uid || receipt.werkerIsRead) return;
    try {
      await updateDoc(
        doc(db, `users/${receipt.schedulerUid}/paymentReceipts/${receipt.receiptId}`),
        { werkerIsRead: true, werkerReadAt: serverTimestamp() }
      );
      await setDoc(
        doc(db, `users/${uid}/users_PaymentReceiptsCount`, uid),
        { paymentReceiptsUnreadCount: increment(-1), uid },
        { merge: true }
      );
    } catch (error) {
      // no-op
    }
  }, [uid]);

  const markAllReceiptsRead = async () => {
    if (!uid) return;
    const unreadReceipts = receipts.filter((r) => !r.werkerIsRead);
    if (!unreadReceipts.length) return;
    try {
      setMarkingAllRead(true);
      const batch = writeBatch(db);
      unreadReceipts.forEach((r) => {
        batch.update(
          doc(db, `users/${r.schedulerUid}/paymentReceipts/${r.receiptId}`),
          { werkerIsRead: true, werkerReadAt: serverTimestamp() }
        );
      });
      await batch.commit();
      await setDoc(
        doc(db, `users/${uid}/users_PaymentReceiptsCount`, uid),
        { paymentReceiptsUnreadCount: increment(-unreadReceipts.length), uid },
        { merge: true }
      );
    } catch (error) {
      // no-op
    } finally {
      setMarkingAllRead(false);
    }
  };

  // ── Notification navigation ───────────────────────────────────────────────────
  const {
    navigateToWerkerShift,
    handleWerkerJobTap,
    handleSchedulerAddedTap,
    handleSchedulerEditedJobTap,
    handleSchedulerRemovedTap,
    handleSchedulerFilledJobTap,
  } = useNotificationTapHandlers(uid ?? "");

  const handleNotificationTap = useCallback(
    async (item: AppNotification) => {
      markOneRead(item.id, item.isRead);
      const details = item.additionalDetails;
      switch (item.type) {
        case PUSH_NOTIFICATION_TYPES.AssociationRequest:
          if (item.fromUID) history.push(`/AssociateProfile/${item.fromUID}`);
          break;
        case PUSH_NOTIFICATION_TYPES.SchedulerNewAssociateFromInvite: {
          const werkerUid = details?.werkerUid || item.fromUID;
          if (werkerUid) history.push(`/AssociateProfile/${werkerUid}`);
          break;
        }
        case PUSH_NOTIFICATION_TYPES.SchedulerFilledJobs:
        case PUSH_NOTIFICATION_TYPES.SchedulerUnFilledJobs:
          if (details?.uniqueJobDocId) await handleSchedulerFilledJobTap(details.uniqueJobDocId);
          break;
        case PUSH_NOTIFICATION_TYPES.SchedulerEditedJob:
          if (details?.werkerJobDocId && details?.uniqueJobDocId) {
            await handleSchedulerEditedJobTap(details.werkerJobDocId, details.uniqueJobDocId);
          }
          break;
        case PUSH_NOTIFICATION_TYPES.SchedulerRemovedWerkerFromShift:
          if (details?.werkerJobDocId && details?.uniqueJobDocId) {
            await handleSchedulerRemovedTap(details.werkerJobDocId, details.uniqueJobDocId);
          }
          break;
        case PUSH_NOTIFICATION_TYPES.SchedulerAddedWerkerToShift:
          if (details?.werkerJobDocId && details?.uniqueJobDocId && details?.schedUid) {
            await handleSchedulerAddedTap(details.werkerJobDocId, details.uniqueJobDocId, details.schedUid);
          } else if (details?.uniqueJobDocId && details?.schedUid) {
            await navigateToWerkerShift(details.uniqueJobDocId, details.schedUid);
          }
          break;
        case PUSH_NOTIFICATION_TYPES.WerkerJobAvailable:
        case PUSH_NOTIFICATION_TYPES.WerkerJobReminder:
          if (details?.uniqueJobDocId && details?.schedUid) {
            await handleWerkerJobTap(details.uniqueJobDocId, details.schedUid);
          }
          break;
        default:
          break;
      }
    },
    [history, markOneRead, navigateToWerkerShift, handleWerkerJobTap, handleSchedulerFilledJobTap, handleSchedulerAddedTap, handleSchedulerRemovedTap, handleSchedulerEditedJobTap]
  );

  const unreadBorderColor = `var(--ion-color-${profile?.UserLastRoleScheduler ? 'secondwarning' : 'warning'})`;

  return (
    <IonPage>
      <WerkerSchedulerHeader title="Notifications" />

      {/* Segment toggle */}
      <div style={{ paddingTop: 10, paddingBottom: 4 }}>
          <IonSegment
            value={activeTab}
            onIonChange={(e: any) => setActiveTab(e.detail.value)}
            style={{ width: '100%' }}
          >
            <IonSegmentButton value="notifications">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IonIcon icon={notificationsOutline} />
                <span>Notifications</span>
                {unreadNotificationsCount > 0 && (
                  <IonBadge color="danger" style={{ fontSize: '0.7rem', padding: '2px 5px' }}>
                    {unreadNotificationsCount}
                  </IonBadge>
                )}
              </div>
            </IonSegmentButton>
            <IonSegmentButton value="receipts">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IonIcon icon={cashOutline} />
                <span>Receipts</span>
                {unreadReceiptsCount > 0 && (
                  <IonBadge color="danger" style={{ fontSize: '0.7rem', padding: '2px 5px' }}>
                    {unreadReceiptsCount}
                  </IonBadge>
                )}
              </div>
            </IonSegmentButton>
          </IonSegment>
      </div>

      <IonContent>

        {/* ── NOTIFICATIONS TAB ── */}
        {activeTab === 'notifications' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 10px 0 10px' }}>
              <IonButton
                size="small"
                fill="outline"
                onClick={markAllNotificationsRead}
                disabled={markingAllRead || unreadNotificationsCount === 0}
              >
                <IonIcon icon={checkmarkDoneOutline} slot="start" />
                {markingAllRead ? 'Working...' : 'Mark All Read'}
              </IonButton>
            </div>

            {notificationsLoading ? (
              <div style={{ padding: '24px 16px', display: 'flex', justifyContent: 'center' }}>
                <IonSpinner name="crescent" />
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <IonIcon icon={notificationsOutline} style={{ fontSize: 42, opacity: 0.65, marginBottom: 10 }} />
                <IonText><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>No notifications yet</h2></IonText>
                <IonText color="medium"><p style={{ marginTop: 8 }}>Important non-chat events will appear here.</p></IonText>
              </div>
            ) : (
              <IonList lines="none" style={{ padding: '8px 8px 20px 8px' }}>
                {notifications.map((item) => {
                  const isUnread = !item.isRead;
                  return (
                    <IonCard
                      key={item.id}
                      button={true}
                      onClick={() => handleNotificationTap(item)}
                      style={{
                        margin: '8px 4px',
                        borderLeft: isUnread ? `4px solid ${unreadBorderColor}` : '4px solid transparent',
                        borderRadius: '5px',
                        opacity: isUnread ? 1 : 0.9,
                      }}
                    >
                      <IonCardContent style={{ padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                            <IonIcon
                              icon={getNotificationIcon(item.type)}
                              color="medium"
                              style={{ fontSize: 22, minWidth: 22, marginTop: 2 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <IonText>
                                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: isUnread ? 700 : 600, marginBottom: 4 }}>
                                  {item.title || 'Notification'}
                                </h3>
                              </IonText>
                              <IonText color="medium">
                                <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.35, whiteSpace: 'normal' }}>
                                  {item.body || ''}
                                </p>
                              </IonText>
                            </div>
                          </div>
                          <IonText color="medium">
                            <p style={{ margin: 0, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {formatNotificationTime(item.createdAt)}
                            </p>
                          </IonText>
                        </div>
                      </IonCardContent>
                    </IonCard>
                  );
                })}
              </IonList>
            )}
          </>
        )}

        {/* ── RECEIPTS TAB ── */}
        {activeTab === 'receipts' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 10px 0 10px' }}>
              <IonButton
                size="small"
                fill="outline"
                onClick={markAllReceiptsRead}
                disabled={markingAllRead || unreadReceiptsCount === 0}
              >
                <IonIcon icon={checkmarkDoneOutline} slot="start" />
                {markingAllRead ? 'Working...' : 'Mark All Read'}
              </IonButton>
            </div>

            {receiptsLoading ? (
              <div style={{ padding: '24px 16px', display: 'flex', justifyContent: 'center' }}>
                <IonSpinner name="crescent" />
              </div>
            ) : receipts.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <IonIcon icon={cashOutline} style={{ fontSize: 42, opacity: 0.65, marginBottom: 10 }} />
                <IonText><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>No payment receipts</h2></IonText>
                <IonText color="medium"><p style={{ marginTop: 8 }}>Payment receipts from schedulers will appear here.</p></IonText>
              </div>
            ) : (
              <IonList lines="none" style={{ padding: '8px 8px 20px 8px' }}>
                {receipts.map((receipt) => {
                  const isUnread = !receipt.werkerIsRead;
                  const schedulerFirstName = receipt.schedulerName?.split(' ')[0] || receipt.schedulerName || 'Scheduler';
                  const jobCount = receipt.jobs?.length ?? 0;
                  return (
                    <IonCard
                      key={receipt.receiptId}
                      button={true}
                      onClick={() => history.push(`/notifications/receipt/${receipt.schedulerUid}/${receipt.receiptId}`)}
                      style={{
                        margin: '8px 4px',
                        borderLeft: isUnread ? `4px solid ${unreadBorderColor}` : '4px solid transparent',
                        borderRadius: '5px',
                        opacity: isUnread ? 1 : 0.9,
                      }}
                    >
                      <IonCardContent style={{ padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                            <IonIcon
                              icon={cashOutline}
                              color="medium"
                              style={{ fontSize: 22, minWidth: 22, marginTop: 2 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <IonText>
                                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: isUnread ? 700 : 600, marginBottom: 4 }}>
                                  Payment from {schedulerFirstName}
                                </h3>
                              </IonText>
                              <IonText color="medium">
                                <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.35 }}>
                                  {formatCurrency(receipt.totalAmount)} &middot; {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
                                </p>
                              </IonText>
                            </div>
                          </div>
                          <IonText color="medium">
                            <p style={{ margin: 0, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {formatNotificationTime(receipt.createdAt)}
                            </p>
                          </IonText>
                        </div>
                      </IonCardContent>
                    </IonCard>
                  );
                })}
              </IonList>
            )}
          </>
        )}

      </IonContent>
      <TabNavigator />
    </IonPage>
  );
};

const Notifications: React.FC = () => {
  if (isDesktop()) {
    return <NotificationsDesktop />;
  }

  return <NotificationsMobile />;
};

export default Notifications;
